import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { 
  Home, History, User, Wallet, Gift, Trophy, Star, Flame,
  ChevronRight, ChevronDown, CheckCircle, XCircle, Clock, 
  Gamepad2, Dice1, Play, Zap, TrendingUp, Search, Menu,
  ArrowDownLeft, ArrowUpRight, Shield, Bell, Phone, MessageCircle,
  CreditCard, Globe, Settings, LogOut, Crown, Target, Award
} from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function OneWinCombinedPreview() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState('main');
  const [showFrame, setShowFrame] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all'); // all, deposit, withdrawal
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const pageParam = searchParams.get('page');
    const frameParam = searchParams.get('frame');
    
    const pathParts = location.pathname.split('/');
    const pathPage = pathParts[pathParts.length - 1];
    const pageMap = { main: 'main', history: 'history', profile: 'profile', wallet: 'wallet', combined: 'main' };
    
    if (pageParam) setCurrentPage(pageMap[pageParam] || pageParam);
    else if (pathPage && pageMap[pathPage]) setCurrentPage(pageMap[pathPage]);
    
    if (frameParam !== null) setShowFrame(frameParam !== 'false' && frameParam !== '0');
    const filterParam = searchParams.get('filter');
    if (filterParam && ['all', 'deposit', 'withdrawal'].includes(filterParam)) setHistoryFilter(filterParam);
    if (dataParam) { try { 
      const parsed = (() => { try { return JSON.parse(dataParam); } catch(_) { return JSON.parse(decodeURIComponent(dataParam)); } })();
      setData(parsed);
      if (parsed.defaultFilter && ['all', 'deposit', 'withdrawal'].includes(parsed.defaultFilter)) {
        setHistoryFilter(parsed.defaultFilter);
      }
    } catch (e) {} }
  }, [searchParams, location.pathname]);

  // Set initial date filter from transaction range
  useEffect(() => {
    if (!data || !data.transactions || data.transactions.length === 0) return;
    const parseD = (d) => {
      if (!d) return 0;
      const p = d.split('.');
      if (p.length === 3) return new Date(`${p[2]}-${p[1]}-${p[0]}`).getTime();
      return 0;
    };
    const dates = data.transactions.map(t => parseD(t.date)).filter(d => d > 0);
    if (dates.length === 0) return;
    const minD = new Date(Math.min(...dates));
    const maxD = new Date(Math.max(...dates));
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    if (!dateFrom) setDateFrom(toISO(minD));
    if (!dateTo) setDateTo(toISO(maxD));
  }, [data]);

  if (!data) return (
    <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const visibleId = data.visibleId || Math.floor(100000 + Math.random() * 900000);
  const balance = parseFloat(data.balance || 0);
  const bonusBalance = parseFloat(data.bonusBalance || 0);
  const totalBalance = balance + bonusBalance;
  const fmt = (n) => parseFloat(n || 0).toLocaleString('ru-RU');

  // 1Win Games data with real colors
  const games1win = [
    { name: 'LUCKY JET', color: 'from-blue-600 to-purple-600', img: '🚀' },
    { name: 'SPEED-N-CASH', color: 'from-purple-600 to-pink-500', img: '🏎️' },
    { name: 'ROCKET QUEEN', color: 'from-orange-500 to-red-500', img: '👸' },
    { name: 'CRASH', color: 'from-green-400 to-green-500', img: '📈' },
    { name: 'COINFLIP', color: 'from-yellow-500 to-orange-500', img: '🪙' },
    { name: 'PENALTY', color: 'from-blue-500 to-cyan-400', img: '⚽' },
    { name: 'MINES', color: 'from-green-600 to-emerald-500', img: '💎' },
    { name: 'KENO', color: 'from-indigo-500 to-purple-500', img: '🎱' },
  ];

  // Browser Frame
  const BrowserFrame = () => (
    <div className="flex-shrink-0" style={{ backgroundColor: '#202124' }}>
      <div className="flex items-center h-[38px] pl-2">
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[160px]" style={{ backgroundColor: '#35363a' }}>
          <div className="w-4 h-4 mr-2 rounded-sm bg-[#1a1f2e] flex items-center justify-center">
            <span className="text-[8px] font-black text-white">1W</span>
          </div>
          <span className="text-[13px] text-gray-300">1win - Pair for your passion</span>
        </div>
      </div>
      <div className="flex items-center h-[40px] px-4 gap-4" style={{ backgroundColor: '#292a2d' }}>
        <div className="flex gap-2">
          {['←', '→', '↻'].map((a, i) => (
            <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 text-gray-400">{a}</button>
          ))}
        </div>
        <div className="flex-1 h-[32px] rounded-full px-4 flex items-center" style={{ backgroundColor: '#202124' }}>
          <span className="text-[14px] text-gray-400">🔒 {currentPage === 'history' ? '1win.com/history' : currentPage === 'profile' ? '1win.com/profile' : currentPage === 'wallet' ? '1win.com/wallet' : '1win.com'}</span>
        </div>
      </div>
    </div>
  );

  // Sidebar - Real 1Win style
  const Sidebar = () => (
    <div className={`${sidebarCollapsed ? 'w-16' : 'w-60'} flex-shrink-0 flex flex-col transition-all duration-300`} 
      style={{ backgroundColor: '#1a1f2e' }}>
      
      {/* User/Login area */}
      <div className="p-3 border-b border-white/10">
        <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
          <div className="w-9 h-9 rounded-full bg-gray-600 flex items-center justify-center">
            <User className="w-5 h-5 text-gray-300" />
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 text-left">
              <div className="text-white text-sm font-medium">{accountName}</div>
              <div className="text-gray-500 text-xs">ID: {visibleId}</div>
            </div>
          )}
          {!sidebarCollapsed && <ChevronRight className="w-4 h-4 text-gray-500" />}
        </button>
      </div>

      {/* Free money banner */}
      {!sidebarCollapsed && (
        <div className="mx-3 mt-3 p-3 rounded-lg relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
          <div className="text-white text-xs font-medium">Бесплатные деньги</div>
          <div className="absolute right-0 top-0 w-16 h-16">
            <span className="text-3xl">💵</span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto">
        {[
          { id: 'casino', icon: Dice1, label: 'Казино', hasSubmenu: true },
          { id: 'sports', icon: Trophy, label: 'Спорт', hasSubmenu: true },
          { id: 'markets', icon: TrendingUp, label: 'Маркеты' },
          { id: 'bonuses', icon: Gift, label: 'Бонусы', badge: '1' },
          { id: 'vip', icon: Crown, label: 'VIP клуб' },
          { divider: true },
          { id: 'promotions', icon: Star, label: 'Акции' },
          { id: 'blog', icon: MessageCircle, label: 'Блог' },
          { id: 'poker', icon: Gamepad2, label: 'Покер' },
          { id: 'vsport', icon: Zap, label: 'Вспорт' },
          { id: 'fantasy', icon: Target, label: 'Фэнтези спорт' },
        ].map((item, i) => {
          if (item.divider) return <div key={i} className="my-2 mx-3 border-t border-white/10" />;
          return (
            <button key={i}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <>
                  <span className="flex-1 text-left text-sm">{item.label}</span>
                  {item.badge && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{item.badge}</span>
                  )}
                  {item.hasSubmenu && <ChevronDown className="w-4 h-4" />}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* App download */}
      {!sidebarCollapsed && (
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
            <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-white text-xs font-medium">1win для ОС</div>
              <div className="text-gray-500 text-[10px]">Мгновенный доступ к платформе</div>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </div>
        </div>
      )}

      {/* Social & Support */}
      {!sidebarCollapsed && (
        <div className="p-3 border-t border-white/10">
          <div className="flex gap-2 mb-3">
            {['WhatsApp', 'Telegram', 'Instagram'].map((s, i) => (
              <div key={i} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white text-xs">
                {s[0]}
              </div>
            ))}
          </div>
          <button className="flex items-center gap-2 text-gray-400 text-sm hover:text-white">
            <Phone className="w-4 h-4" />
            <span>Поддержка</span>
            <span className="ml-auto px-1.5 py-0.5 rounded text-[10px] bg-green-500 text-white">24/7</span>
          </button>
        </div>
      )}

      {/* Frame toggle */}
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center justify-between">
          {!sidebarCollapsed && <span className="text-gray-500 text-xs">Рамка</span>}
          <Switch checked={showFrame} onCheckedChange={setShowFrame} />
        </div>
      </div>
    </div>
  );

  // Top Header
  const Header = () => (
    <header className="h-14 px-4 flex items-center justify-between" style={{ backgroundColor: '#1a1f2e', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
      {/* Left - Logo & Nav */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-black text-white">1win</span>
        </div>
        
        <nav className="flex items-center gap-1">
          {[
            { id: 'home', label: 'Главная', icon: Home, active: currentPage === 'main' },
            { id: 'casino', label: 'Казино', icon: Dice1 },
            { id: 'free', label: 'Бесплатно', icon: Gift },
            { id: 'sports', label: 'Спорт', icon: Trophy },
          ].map(item => (
            <button key={item.id}
              onClick={() => item.id === 'home' && setCurrentPage('main')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                item.active ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Right - Balance & User */}
      <div className="flex items-center gap-3">
        {/* Balance */}
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: '#252a3a' }}>
          <Wallet className="w-4 h-4 text-gray-400" />
          <span className="text-white font-bold">{fmt(totalBalance)} ₽</span>
          <button className="ml-2 w-6 h-6 rounded bg-white/10 flex items-center justify-center text-white hover:bg-white/20">
            +
          </button>
        </div>

        {/* Deposit button */}
        <button className="px-5 py-2 rounded-lg text-white text-sm font-bold"
          style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
          Пополнить
        </button>

        {/* User avatar */}
        <button onClick={() => setCurrentPage('profile')}
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <span className="text-white text-xs font-bold">{accountName.charAt(0)}</span>
          </div>
          <span className="text-white text-sm font-medium">{accountName}</span>
        </button>
      </div>
    </header>
  );

  // Main Page - 1Win style
  const MainPage = () => (
    <div className="flex-1 overflow-auto" style={{ backgroundColor: '#1a1f2e' }}>
      <div className="p-6">
        {/* Hero Banner Section */}
        <div className="flex gap-4 mb-6">
          {/* Main Banner */}
          <div className="flex-1 rounded-2xl p-8 relative overflow-hidden" 
            style={{ background: 'linear-gradient(135deg, #1e2433 0%, #252a3a 100%)', minHeight: '280px' }}>
            <div className="relative z-10">
              <h2 className="text-4xl font-bold text-white mb-2">Выигрывай</h2>
              <h2 className="text-4xl font-bold text-white mb-4">вместе с нами</h2>
              <h2 className="text-5xl font-black text-white mb-6">{data.firstName || 'Игрок'}</h2>
              <button className="px-6 py-3 rounded-lg bg-white text-black font-bold hover:bg-gray-100 transition-colors">
                Сделать ставку
              </button>
            </div>
            <div className="absolute right-0 bottom-0 w-64 h-64 opacity-20">
              <div className="w-full h-full bg-gradient-to-br from-green-500 to-transparent rounded-full blur-3xl"></div>
            </div>
          </div>

          {/* Side Banners */}
          <div className="w-80 flex flex-col gap-4">
            {/* Free money */}
            <div className="rounded-xl p-4 relative overflow-hidden" 
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', height: '130px' }}>
              <div className="text-white/80 text-xs font-medium mb-1">Бесплатно</div>
              <div className="text-white text-sm">Раздаём</div>
              <div className="text-white text-sm">Ferrari и</div>
              <div className="text-white text-sm">другие призы</div>
              <div className="absolute right-2 bottom-2 text-4xl">🚗</div>
            </div>
            
            {/* Bonuses */}
            <div className="rounded-xl p-4 relative overflow-hidden" 
              style={{ background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)', height: '130px' }}>
              <div className="text-white/80 text-xs font-medium mb-1">Бонусы</div>
              <div className="flex items-center gap-1 mt-auto">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-white text-xs">{bonusBalance > 0 ? '1 доступный бонус' : 'Нет бонусов'}</span>
              </div>
              <div className="absolute right-2 bottom-2 text-4xl">🎁</div>
            </div>
          </div>
        </div>

        {/* 1win games section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xl font-black text-white">1W</span>
              <span className="text-xl font-bold text-white">Игры 1win</span>
            </div>
            <button className="flex items-center gap-1 text-gray-400 hover:text-white text-sm">
              Все игры <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-8 gap-3">
            {games1win.map((game, i) => (
              <div key={i} className={`rounded-xl overflow-hidden cursor-pointer hover:scale-105 transition-transform bg-gradient-to-br ${game.color}`}>
                <div className="h-24 flex items-center justify-center text-4xl">
                  {game.img}
                </div>
                <div className="p-2 bg-black/20">
                  <div className="text-white text-xs font-bold text-center">{game.name}</div>
                  <div className="text-white/60 text-[10px] text-center">1WIN GAMES</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* User Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="rounded-xl p-4" style={{ backgroundColor: '#252a3a' }}>
            <div className="text-gray-500 text-sm mb-1">Баланс</div>
            <div className="text-2xl font-bold text-white">{fmt(balance)} ₽</div>
          </div>
          <div className="rounded-xl p-4" style={{ backgroundColor: '#252a3a' }}>
            <div className="text-gray-500 text-sm mb-1">Бонусы</div>
            <div className="text-2xl font-bold text-yellow-400">{fmt(bonusBalance)} ₽</div>
          </div>
          <div className="rounded-xl p-4" style={{ backgroundColor: '#252a3a' }}>
            <div className="text-gray-500 text-sm mb-1">Всего</div>
            <div className="text-2xl font-bold text-green-400">{fmt(totalBalance)} ₽</div>
          </div>
          <div className="rounded-xl p-4" style={{ backgroundColor: '#252a3a' }}>
            <div className="text-gray-500 text-sm mb-1">Статус</div>
            <div className="text-2xl font-bold text-white flex items-center gap-2">
              <Crown className="w-6 h-6 text-yellow-400" /> VIP
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="rounded-xl p-5" style={{ backgroundColor: '#252a3a' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold">Последние операции</h3>
            <button onClick={() => setCurrentPage('history')} className="text-green-400 text-sm hover:text-green-300">
              Вся история →
            </button>
          </div>
          <div className="space-y-2">
            {(data.transactions || data.bets || []).slice(0, 5).map((tx, i) => {
              const isDeposit = tx.type === 'deposit' || tx.type === 'win';
              return (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: '#1a1f2e' }}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDeposit ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      {isDeposit ? <ArrowDownLeft className="w-5 h-5 text-green-400" /> : <ArrowUpRight className="w-5 h-5 text-red-400" />}
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">{isDeposit ? 'Пополнение' : 'Вывод'}</div>
                      <div className="text-gray-500 text-xs">{tx.date} {tx.time} · Ozon {(((tx.category || "").trim() === "Финансовые услуги") ? "Карта" : (((tx.category || "").trim() === "Пополнения") ? "СБП" : ((tx.method === "Карта" || tx.method === "СБП") ? tx.method : "СБП")))}</div>
                    </div>
                  </div>
                  <div className={`font-bold ${isDeposit ? 'text-green-400' : 'text-red-400'}`}>
                    {isDeposit ? '+' : '-'}{fmt(tx.amount)} ₽
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  // History Page
  const HistoryPage = () => {
    const allTx = data.transactions || data.bets || [];
    
    // Parse helper
    const parseD = (d) => {
      if (!d) return 0;
      const p = d.split('.');
      if (p.length === 3) return new Date(`${p[2]}-${p[1]}-${p[0]}`).getTime();
      return 0;
    };

    // Filter by type
    let filtered = allTx;
    if (historyFilter === 'deposit') filtered = allTx.filter(t => t.type === 'deposit' || t.type === 'win');
    else if (historyFilter === 'withdrawal') filtered = allTx.filter(t => t.type === 'withdrawal' || t.type === 'loss');
    
    // Filter by date
    if (dateFrom) {
      const fromTs = new Date(dateFrom).getTime();
      filtered = filtered.filter(t => parseD(t.date) >= fromTs);
    }
    if (dateTo) {
      const toTs = new Date(dateTo).getTime() + 86400000; // include end day
      filtered = filtered.filter(t => parseD(t.date) < toTs);
    }

    // Pagination: 10 per page via ?pageNum=1|2
    const PER_PAGE = 10;
    const pageNumParam = parseInt(searchParams.get('pageNum') || '0', 10);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const activePage = pageNumParam > 0 ? Math.min(pageNumParam, totalPages) : 0;
    const visibleTx = activePage > 0
      ? filtered.slice((activePage - 1) * PER_PAGE, activePage * PER_PAGE)
      : filtered;

    // Суммы считаем из ВСЕХ транзакций (не только отфильтрованных)
    const allDeposits = allTx.filter(t => t.type === 'deposit' || t.type === 'win');
    const allWithdrawals = allTx.filter(t => t.type === 'withdrawal' || t.type === 'loss');
    const totalDeposits = data.depositsTotal || allDeposits.reduce((s, t) => s + parseFloat(t.amount || 0), 0);
    const totalWithdrawals = data.withdrawalsTotal || allWithdrawals.reduce((s, t) => s + parseFloat(t.amount || 0), 0);

    return (
      <div className="flex-1 p-6 overflow-auto" style={{ backgroundColor: '#1a1f2e' }}>
        <h1 className="text-2xl font-bold text-white mb-6">История операций</h1>

        {/* Filters */}
        <div className="rounded-xl p-4 mb-6" style={{ backgroundColor: '#252a3a' }}>
          <div className="flex flex-wrap items-center gap-4">
            {/* Type filter */}
            <div className="flex rounded-lg overflow-hidden border border-white/10">
              {[
                { id: 'all', label: 'Все' },
                { id: 'deposit', label: 'Пополнения' },
                { id: 'withdrawal', label: 'Выводы' },
              ].map(f => (
                <button key={f.id} onClick={() => setHistoryFilter(f.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    historyFilter === f.id ? 'bg-green-500 text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}>{f.label}</button>
              ))}
            </div>

            {/* Date filters — display in ru-RU format: "с {first row date} по {last row date}" */}
            <div className="flex items-center gap-2 ml-auto">
              {(() => {
                const fmtRu = (s) => {
                  if (!s) return '';
                  // If already DD.MM.YYYY, return as is
                  if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) return s;
                  // If YYYY-MM-DD, convert
                  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
                  return s;
                };
                // Dates from ALL filtered txs (not page-scoped) — stable across pagination
                const firstDate = filtered && filtered.length ? filtered[0].date : '';
                const lastDate = filtered && filtered.length ? filtered[filtered.length - 1].date : '';
                return (<>
                  <span className="text-gray-400 text-sm">с</span>
                  <div className="bg-[#1a1f2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">{fmtRu(lastDate) || fmtRu(dateFrom)}</div>
                  <span className="text-gray-400 text-sm">по</span>
                  <div className="bg-[#1a1f2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">{fmtRu(firstDate) || fmtRu(dateTo)}</div>
                </>);
              })()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Всего', value: filtered.length, color: 'text-white' },
            { label: 'Пополнения', value: `+${fmt(totalDeposits)} ₽`, color: 'text-green-400' },
            { label: 'Выводы', value: `-${fmt(totalWithdrawals)} ₽`, color: 'text-red-400' },
            { label: 'Итого', value: `${totalDeposits - totalWithdrawals >= 0 ? '+' : ''}${fmt(totalDeposits - totalWithdrawals)} ₽`, 
              color: totalDeposits - totalWithdrawals >= 0 ? 'text-green-400' : 'text-red-400' },
          ].map((stat, i) => (
            <div key={i} className="rounded-xl p-4" style={{ backgroundColor: '#252a3a' }}>
              <div className="text-gray-500 text-sm mb-1">{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: '#252a3a' }}>
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <History className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <div className="text-lg font-medium text-white mb-2">Нет операций</div>
              <div className="text-gray-500">Измените фильтры для отображения</div>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {visibleTx.map((tx, i) => {
                const isDeposit = tx.type === 'deposit' || tx.type === 'win';
                return (
                  <div key={i} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDeposit ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                        {isDeposit ? <CheckCircle className="w-6 h-6 text-green-400" /> : <XCircle className="w-6 h-6 text-red-400" />}
                      </div>
                      <div>
                        <div className="text-white font-medium">{isDeposit ? 'Пополнение' : 'Вывод средств'}</div>
                        <div className="text-gray-500 text-sm">{tx.date} {tx.time} · Ozon {(((tx.category || "").trim() === "Финансовые услуги") ? "Карта" : (((tx.category || "").trim() === "Пополнения") ? "СБП" : ((tx.method === "Карта" || tx.method === "СБП") ? tx.method : "СБП")))}</div>
                      </div>
                    </div>
                    <div className={`text-lg font-bold ${isDeposit ? 'text-green-400' : 'text-red-400'}`}>
                      {isDeposit ? '+' : '-'}{fmt(tx.amount)} ₽
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {activePage > 0 && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <div key={p}
                className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium ${
                  p === activePage ? 'bg-green-500 text-white' : 'bg-[#252a3a] text-gray-400 border border-white/10'
                }`}>{p}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Wallet Page
  const WalletPage = () => (
    <div className="flex-1 p-6 overflow-auto" style={{ backgroundColor: '#1a1f2e' }}>
      <h1 className="text-2xl font-bold text-white mb-6">Касса</h1>

      <div className="rounded-2xl p-6 mb-6 relative overflow-hidden" 
        style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
        <div className="text-white/80 text-sm mb-2">Доступный баланс</div>
        <div className="text-4xl font-black text-white mb-4">{fmt(totalBalance)} ₽</div>
        <div className="flex gap-6 text-sm">
          <div><span className="text-white/60">Основной:</span> <span className="text-white font-bold">{fmt(balance)} ₽</span></div>
          <div><span className="text-yellow-300/80">Бонусы:</span> <span className="text-yellow-300 font-bold">{fmt(bonusBalance)} ₽</span></div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="rounded-xl p-5" style={{ backgroundColor: '#252a3a' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <ArrowDownLeft className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-white font-bold">Пополнение</div>
          </div>
          {['Банковская карта', 'СБП', 'Криптовалюта', 'QIWI'].map((m, i) => (
            <button key={i} className="w-full p-3 mb-2 rounded-lg text-white text-left hover:bg-white/5 flex items-center justify-between"
              style={{ backgroundColor: '#1a1f2e' }}>
              {m} <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          ))}
        </div>

        <div className="rounded-xl p-5" style={{ backgroundColor: '#252a3a' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <ArrowUpRight className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-white font-bold">Вывод средств</div>
          </div>
          {['На карту', 'СБП', 'Криптовалюта', 'QIWI'].map((m, i) => (
            <button key={i} className="w-full p-3 mb-2 rounded-lg text-white text-left hover:bg-white/5 flex items-center justify-between"
              style={{ backgroundColor: '#1a1f2e' }}>
              {m} <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // Profile Page
  const ProfilePage = () => (
    <div className="flex-1 p-6 overflow-auto" style={{ backgroundColor: '#1a1f2e' }}>
      <h1 className="text-2xl font-bold text-white mb-6">Профиль</h1>

      <div className="grid grid-cols-3 gap-6">
        <div className="rounded-xl p-6 text-center" style={{ backgroundColor: '#252a3a' }}>
          <div className="w-20 h-20 rounded-full mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-3xl font-bold text-white">
            {accountName.charAt(0)}
          </div>
          <div className="text-xl font-bold text-white mb-1">{accountName}</div>
          <div className="text-gray-500 mb-3">ID: {visibleId}</div>
          <div className="flex items-center justify-center gap-2">
            <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Верифицирован
            </span>
          </div>
        </div>

        <div className="col-span-2 space-y-3">
          {[
            { label: 'ФИО', value: accountName },
            { label: 'Email', value: data.email || 'Не указан' },
            { label: 'Телефон', value: data.phone || 'Не указан' },
            { label: 'Баланс', value: `${fmt(totalBalance)} ₽` },
            { label: 'Бонусы', value: `${fmt(bonusBalance)} ₽` },
          ].map((item, i) => (
            <div key={i} className="rounded-lg p-4 flex items-center justify-between" style={{ backgroundColor: '#252a3a' }}>
              <span className="text-gray-500">{item.label}</span>
              <span className="text-white font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1a1f2e' }}>
      {showFrame && <BrowserFrame />}
      <div className="flex" style={{ minHeight: showFrame ? 'calc(100vh - 78px)' : '100vh' }}>
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          {currentPage === 'main' && <MainPage />}
          {currentPage === 'history' && <HistoryPage />}
          {currentPage === 'wallet' && <WalletPage />}
          {currentPage === 'profile' && <ProfilePage />}
        </div>
      </div>
    </div>
  );
}
